import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Plus, ClipboardList, Play, Pause, CheckCircle, Loader2, Calculator, Printer, Download, Pencil, Trash2, History, Archive, BarChart3, Package, Activity, Camera, StickyNote, Scale } from "lucide-react";
import { WeightHistoryDialog } from "../components/WeightHistoryDialog";
import OrderTimeline from "../components/OrderTimeline";
import OrderPhotos from "../components/OrderPhotos";
import PackagingProgress from "../components/PackagingProgress";
import ClientQuickCreateDialog from "../components/ClientQuickCreateDialog";
import ShiftNotes from "../components/ShiftNotes";
import OrderActions from "../components/OrderActions";
import { showError } from "../lib/errorHandler";
import { openPackingSlip } from "../lib/packingSlip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a6fd91e7-1ad0-4cf0-91ce-b769f9bab27a/artifacts/m0y29wqk_Sem%20T%C3%ADtulo.png";

const STATUS_CONFIG = {
  pending: { label: "Pendente", color: "bg-slate-500" },
  running: { label: "Em Execução", color: "bg-emerald-500" },
  paused: { label: "Pausada", color: "bg-amber-500" },
  suspended: { label: "Suspensa", color: "bg-orange-500" },
  completed: { label: "Concluída", color: "bg-blue-500" },
  cancelled: { label: "Cancelada", color: "bg-red-500" },
  archived: { label: "Arquivada", color: "bg-gray-400" },
};

const PRIORITY_CONFIG = {
  urgent: { label: "URGENTE", className: "bg-red-600 text-white animate-pulse", icon: "🔥" },
  high: { label: "Alta", className: "bg-orange-500 text-white", icon: "⚡" },
  normal: { label: "", className: "", icon: "" },
  low: { label: "Baixa", className: "bg-slate-300 text-slate-700", icon: "" },
};

function MaterialBalanceCell({ balance, code }) {
  if (!balance) return <span className="text-xs text-muted-foreground">-</span>;
  const dev = balance.deviation_pct || 0;
  const devColor = Math.abs(dev) < 3 ? "text-emerald-600" : Math.abs(dev) < 8 ? "text-amber-600" : "text-red-600";
  const weightLabel = [
    balance.unit_weight_measured_g ? `Peça medida: ${balance.unit_weight_measured_g} g (teórico ${balance.unit_weight_theoretical_g} g)` : `Peça: peso teórico ${balance.unit_weight_theoretical_g} g (sem pesagem)`,
    balance.shot_weight_measured_g ? `Moldação c/ gito medida: ${balance.shot_weight_measured_g} g (teórico ${balance.shot_weight_theoretical_g} g) — usada no cálculo real` : `Moldação c/ gito teórica: ${balance.shot_weight_theoretical_g} g`,
  ].join("\n");
  return (
    <div className="text-xs leading-tight min-w-[150px]" title={weightLabel} data-testid={`mp-balance-${code}`}>
      <div className="flex justify-between gap-2"><span className="text-muted-foreground">Prevista</span><span className="font-mono font-semibold">{balance.planned_kg.toFixed(1)} kg</span></div>
      <div className="flex justify-between gap-2"><span className="text-muted-foreground">Real</span><span className="font-mono font-semibold">{balance.real_kg.toFixed(1)} kg</span></div>
      <div className="h-1.5 bg-slate-100 rounded mt-1"><div className="h-1.5 rounded bg-blue-500" style={{ width: `${Math.min(100, balance.progress_pct)}%` }} /></div>
      <div className="flex justify-between gap-2 mt-0.5">
        <span className={balance.unit_weight_measured_g || balance.shot_weight_measured_g ? "text-blue-600" : "text-muted-foreground"} data-testid={`mp-weight-${code}`}>
          {balance.shot_weight_measured_g ? `⚖ mold. ${balance.shot_weight_measured_g} g` : balance.unit_weight_measured_g ? `⚖ ${balance.unit_weight_measured_g} g` : `${balance.unit_weight_theoretical_g} g teór.`}
        </span>
        {balance.pieces_produced > 0 && <span className={`font-semibold ${devColor}`}>{dev > 0 ? "+" : ""}{dev}%</span>}
      </div>
    </div>
  );
}

export default function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [materialBalance, setMaterialBalance] = useState({});
  const [weightHistoryOrder, setWeightHistoryOrder] = useState(null);
  const [parts, setParts] = useState([]);
  const [molds, setMolds] = useState([]);
  const [machines, setMachines] = useState([]);
  const [packagingTypes, setPackagingTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showNewClientDialog, setShowNewClientDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [activeTab, setActiveTab] = useState("active"); // active or history
  const [formData, setFormData] = useState({
    part_id: "",
    mold_id: "",
    machine_id: "",
    good_quantity_target: "",
    raw_material_batch: "",
    planned_start: "",
    hours_per_day: "8",
    client_id: "",
    packaging_type_id: "",
    pieces_per_package: "",
    packages_per_pallet: "",
    packaging_notes: "",
  });
  const [calculations, setCalculations] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [bomAvailability, setBomAvailability] = useState(null);
  const [checkingBom, setCheckingBom] = useState(false);
  const [highlightCode, setHighlightCode] = useState(null);

  // Ler ?highlight=CODE do URL e destacar visualmente a ordem
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const h = params.get("highlight");
    if (h) {
      setHighlightCode(h);
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="order-row-${h}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 500);
      // limpar destaque após 5s
      setTimeout(() => setHighlightCode(null), 5000);
    }
  }, []);
  const [showTimeline, setShowTimeline] = useState(false);
  const [timelineOrderId, setTimelineOrderId] = useState(null);
  const [auditOrder, setAuditOrder] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [photosOrder, setPhotosOrder] = useState(null);
  const [packagingOrder, setPackagingOrder] = useState(null);
  const [notesOrder, setNotesOrder] = useState(null);

  const fetchAuditLog = async (orderId) => {
    setAuditLoading(true);
    setAuditLogs([]);
    try {
      const res = await axios.get(`${API}/orders/${orderId}/audit-log`);
      setAuditLogs(res.data.logs || []);
    } catch (e) {
      console.error("Erro audit log:", e);
    } finally {
      setAuditLoading(false);
    }
  };

  // Filter orders by status
  const activeOrders = orders.filter(o => ["pending", "running", "paused"].includes(o.status));
  const historyOrders = orders.filter(o => ["completed", "archived", "cancelled"].includes(o.status));

  const fetchData = async () => {
    try {
      const [ordersRes, partsRes, moldsRes, machinesRes, packagingRes, clientsRes, balanceRes] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/parts`),
        axios.get(`${API}/molds`),
        axios.get(`${API}/machines`),
        axios.get(`${API}/packaging-types`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/material-balance`).catch(() => ({ data: [] })),
      ]);
      setOrders(ordersRes.data);
      setMaterialBalance(Object.fromEntries((balanceRes.data || []).map((b) => [b.order_id, b])));
      setParts(partsRes.data);
      setMolds(moldsRes.data);
      setMachines(machinesRes.data);
      setPackagingTypes(packagingRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      part_id: "",
      mold_id: "",
      machine_id: "",
      good_quantity_target: "",
      raw_material_batch: "",
      planned_start: "",
      hours_per_day: "8",
      packaging_type_id: "",
      pieces_per_package: "",
      packages_per_pallet: "",
      packaging_notes: "",
    });
    setCalculations(null);
    setEditMode(false);
    setEditingOrderId(null);
  };

  const openEditDialog = (order) => {
    setEditMode(true);
    setEditingOrderId(order.id);
    setFormData({
      part_id: order.part_id || "",
      mold_id: order.mold_id || "",
      machine_id: order.machine_id || "",
      good_quantity_target: order.good_quantity_target?.toString() || "",
      raw_material_batch: order.raw_material_batch || "",
      planned_start: order.planned_start || "",
      hours_per_day: order.hours_per_day?.toString() || "8",
      packaging_type_id: order.packaging_type_id || "",
      pieces_per_package: order.pieces_per_package?.toString() || "",
      packages_per_pallet: order.packages_per_pallet?.toString() || "",
      packaging_notes: order.packaging_notes || "",
    });
    setDialogOpen(true);
  };

  // Calculate production values when form changes
  useEffect(() => {
    if (formData.part_id && formData.mold_id && formData.good_quantity_target) {
      const part = parts.find((p) => p.id === formData.part_id);
      const mold = molds.find((m) => m.id === formData.mold_id);
      
      if (part && mold) {
        const goodQty = parseInt(formData.good_quantity_target);
        const scrapPct = part.scrap_percentage;
        const cavities = mold.cavities;
        const cycleTime = mold.cycle_time;
        const partWeight = part.unit_weight;
        const runnerWeight = mold.runner_weight;
        const hoursPerDay = parseFloat(formData.hours_per_day) || 8;
        
        const totalQty = Math.ceil(goodQty / (1 - scrapPct));
        const totalCycles = Math.ceil(totalQty / cavities);
        const totalTimeSeconds = totalCycles * cycleTime;
        const totalTimeHours = totalTimeSeconds / 3600;
        const estimatedDays = Math.max(1, Math.ceil(totalTimeHours / hoursPerDay));
        const weightPerCycle = (partWeight * cavities) + runnerWeight;
        const rawMaterialKg = (weightPerCycle * totalCycles) / 1000;
        
        setCalculations({
          total_quantity: totalQty,
          total_cycles: totalCycles,
          total_time_hours: totalTimeHours.toFixed(2),
          estimated_days: estimatedDays,
          weight_per_cycle: weightPerCycle.toFixed(1),
          raw_material_kg: rawMaterialKg.toFixed(2),
        });
      }
    } else {
      setCalculations(null);
    }
  }, [formData.part_id, formData.mold_id, formData.good_quantity_target, formData.hours_per_day, parts, molds]);

  // BOM availability check para produtos montados
  useEffect(() => {
    const part = parts.find(p => p.id === formData.part_id);
    if (!part || !["assembled", "subassembly"].includes(part.product_type || "") || !formData.good_quantity_target) {
      setBomAvailability(null); return;
    }
    const q = parseFloat(formData.good_quantity_target);
    if (!q || q <= 0) { setBomAvailability(null); return; }
    let cancelled = false;
    setCheckingBom(true);
    axios.get(`${API}/requirements/${formData.part_id}?quantity=${q}`)
      .then(r => { if (!cancelled) setBomAvailability(r.data); })
      .catch(() => { if (!cancelled) setBomAvailability(null); })
      .finally(() => { if (!cancelled) setCheckingBom(false); });
    return () => { cancelled = true; };
  }, [formData.part_id, formData.good_quantity_target, parts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      part_id: formData.part_id,
      mold_id: formData.mold_id,
      machine_id: formData.machine_id,
      good_quantity_target: parseInt(formData.good_quantity_target),
      raw_material_batch: formData.raw_material_batch || null,
      planned_start: formData.planned_start || null,
      hours_per_day: parseFloat(formData.hours_per_day) || 8,
      client_id: formData.client_id || null,
      packaging_type_id: formData.packaging_type_id || null,
      pieces_per_package: formData.pieces_per_package ? parseInt(formData.pieces_per_package) : 0,
      packages_per_pallet: formData.packages_per_pallet ? parseInt(formData.packages_per_pallet) : 0,
      packaging_notes: formData.packaging_notes || null,
    };

    try {
      if (editMode && editingOrderId) {
        // Update existing order
        await axios.put(`${API}/orders/${editingOrderId}`, payload);
        const message = formData.planned_start 
          ? `Ordem atualizada e planeamento ajustado (${calculations?.estimated_days || 1} dias)`
          : "Ordem de produção atualizada com sucesso";
        toast.success(message);
      } else {
        // Create new order
        await axios.post(`${API}/orders`, payload);
        const message = formData.planned_start 
          ? `Ordem criada e adicionada ao planeamento (${calculations?.estimated_days || 1} dias)`
          : "Ordem de produção criada com sucesso";
        toast.success(message);
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (editMode ? "Erro ao atualizar ordem" : "Erro ao criar ordem"));
    }
  };

  const handleStartOrder = async (orderId) => {
    try {
      await axios.post(`${API}/orders/${orderId}/start`);
      toast.success("Ordem iniciada");
      fetchData();
    } catch (error) {
      showError(error, "Erro ao iniciar ordem");
    }
  };

  const generateOrderPDF = async (order) => {
    // ABRIR A JANELA JA para evitar popup blocker (deve ser sync no click)
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`<html><head><title>A gerar...</title></head><body style="font-family:sans-serif;padding:40px;text-align:center;color:#64748b"><h2>A gerar PDF da Ordem ${order.code}...</h2><p>Aguarde alguns segundos.</p></body></html>`);
    }

    const part = parts.find(p => p.id === order.part_id);
    const mold = molds.find(m => m.id === order.mold_id);
    const machine = machines.find(m => m.id === order.machine_id);
    const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;

    // Cores + labels + dots por estado
    const statusStyle = {
      pending:   { label: "PENDENTE",     bg: "#64748b", light: "#f1f5f9", dot: "⚪" },
      running:   { label: "EM EXECUÇÃO",  bg: "#10b981", light: "#ecfdf5", dot: "🟢" },
      paused:    { label: "PAUSADA",      bg: "#f59e0b", light: "#fffbeb", dot: "🟡" },
      completed: { label: "CONCLUÍDA",    bg: "#3b82f6", light: "#eff6ff", dot: "🔵" },
      cancelled: { label: "CANCELADA",    bg: "#ef4444", light: "#fef2f2", dot: "🔴" },
      delivered: { label: "ENTREGUE",     bg: "#8b5cf6", light: "#faf5ff", dot: "🟣" },
    }[order.status] || { label: statusConfig.label, bg: "#64748b", light: "#f1f5f9", dot: "⚪" };

    // Buscar dados extra em paralelo
    let stopsCount = 0;
    let operator = null;
    let timeline = null;
    try {
      const timelineRes = await axios.get(`${API}/orders/${order.id}/timeline`).catch(() => ({data: null}));
      timeline = timelineRes.data;
      stopsCount = timeline?.events?.filter(e => e.type === "stop").length || 0;
    } catch (_) { /* ignore */ }
    if (order.operator_id) {
      try {
        const uRes = await axios.get(`${API}/users`);
        operator = (uRes.data || []).find(u => u.id === order.operator_id);
      } catch (_) { /* ignore */ }
    }

    // Cálculos
    const target = order.good_quantity_target || order.planned_quantity || 0;
    const good = order.current_good_quantity || 0;
    const rejected = order.current_rejected_quantity || 0;
    const progress = target > 0 ? (good / target) * 100 : 0;
    const progressWidth = Math.min(100, progress);

    // Tempo restante estimado
    let timeRemaining = "—";
    let estimatedEnd = "—";
    if (order.estimated_end_time) {
      try {
        const endDt = new Date(order.estimated_end_time);
        const now = new Date();
        const diffMs = endDt - now;
        if (diffMs > 0) {
          const h = Math.floor(diffMs / 3600000);
          const m = Math.floor((diffMs % 3600000) / 60000);
          timeRemaining = `${h}h${String(m).padStart(2, "0")}`;
        } else {
          timeRemaining = "atrasada";
        }
        estimatedEnd = endDt.toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
      } catch (_) {}
    }

    // Contadores de eventos
    const operatorSwitches = Math.max(0, (timeline?.events?.filter(e => e.type === "started").length || 1) - 1);

    // QR Code URL (aponta para a URL da ordem)
    const orderUrl = `${window.location.origin}/orders?highlight=${order.id}`;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(orderUrl)}&margin=0`;

    // Formatar datas
    const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>OF ${order.code} - ${part?.description || ''}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page { size: A4; margin: 12mm; }
          body { font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b; font-size: 11px; }
          .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #1e3a8a; padding-bottom: 12px; margin-bottom: 16px; }
          .header .brand { display: flex; align-items: center; gap: 12px; }
          .header img.logo { height: 50px; }
          .header .brand-title { font-size: 14px; font-weight: 700; color: #1e3a8a; }
          .header .brand-sub { font-size: 9px; color: #64748b; margin-top: 2px; }
          .header .qr { text-align: center; }
          .header .qr img { border: 1px solid #e2e8f0; padding: 3px; background: white; }
          .header .qr .qr-lbl { font-size: 8px; color: #94a3b8; margin-top: 2px; }

          .title-row { display: flex; justify-content: space-between; align-items: center; background: linear-gradient(90deg, #1e3a8a 0%, #3730a3 100%); color: white; padding: 14px 18px; border-radius: 10px; margin-bottom: 14px; }
          .title-row .code { font-size: 26px; font-weight: 900; letter-spacing: 1px; }
          .title-row .part-desc { font-size: 12px; opacity: 0.9; margin-top: 3px; }
          .status-badge { padding: 6px 16px; border-radius: 20px; background: ${statusStyle.bg}; color: white; font-weight: 800; font-size: 12px; letter-spacing: 0.5px; }

          .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 14px; }
          .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin-bottom: 14px; }
          .grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 14px; }

          .card { background: white; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; }
          .card-title { font-size: 9px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.6px; font-weight: 700; margin-bottom: 6px; }
          .card-value { font-size: 15px; font-weight: 700; color: #0f172a; line-height: 1.3; }
          .card-hint { font-size: 10px; color: #64748b; margin-top: 2px; }

          .kpi { text-align: center; padding: 12px 8px; background: white; border: 1px solid #e2e8f0; border-radius: 8px; }
          .kpi .num { font-size: 22px; font-weight: 900; }
          .kpi .lbl { font-size: 9px; color: #94a3b8; text-transform: uppercase; margin-top: 2px; letter-spacing: 0.4px; }

          .progress-block { background: white; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; margin-bottom: 14px; }
          .progress-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 8px; }
          .progress-head .lbl { font-size: 10px; color: #94a3b8; text-transform: uppercase; font-weight: 700; letter-spacing: 0.5px; }
          .progress-head .val { font-size: 20px; font-weight: 900; color: #1e3a8a; }
          .progress-bar-outer { height: 22px; background: #f1f5f9; border-radius: 6px; overflow: hidden; position: relative; }
          .progress-bar-inner { height: 100%; background: linear-gradient(90deg, #10b981 0%, #059669 100%); border-radius: 6px; display: flex; align-items: center; padding-left: 10px; color: white; font-weight: 700; font-size: 11px; transition: width 0.3s; }
          .progress-abs { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); color: #475569; font-weight: 700; font-size: 11px; }

          .part-block { background: ${statusStyle.light}; border: 1px solid ${statusStyle.bg}33; border-radius: 10px; padding: 14px; display: flex; gap: 14px; align-items: center; margin-bottom: 14px; }
          .part-block .photo { width: 90px; height: 90px; border-radius: 8px; background: white; border: 1px solid #e2e8f0; overflow: hidden; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
          .part-block .photo img { max-width: 100%; max-height: 100%; object-fit: contain; }
          .part-block .photo .placeholder { font-size: 22px; color: #cbd5e1; font-weight: 900; }
          .part-block .info { flex: 1; }
          .part-block .code { font-size: 22px; font-weight: 900; color: #1e3a8a; }
          .part-block .desc { font-size: 13px; color: #334155; font-weight: 600; margin-top: 2px; }
          .part-block .meta { font-size: 10px; color: #64748b; margin-top: 4px; display: flex; gap: 12px; flex-wrap: wrap; }

          .section { margin-bottom: 14px; }
          .section-title { font-size: 10px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.6px; font-weight: 700; margin-bottom: 8px; border-bottom: 2px solid #e2e8f0; padding-bottom: 4px; }

          .checklist { background: white; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 16px; }
          .checklist ul { list-style: none; }
          .checklist li { padding: 5px 0; border-bottom: 1px dashed #e2e8f0; font-size: 11px; }
          .checklist li:last-child { border-bottom: none; }
          .checklist .box { display: inline-block; width: 14px; height: 14px; border: 2px solid #94a3b8; margin-right: 8px; vertical-align: middle; border-radius: 2px; }

          .history { background: #f8fafc; border-left: 4px solid #6366f1; padding: 10px 14px; border-radius: 6px; }
          .history-row { display: flex; justify-content: space-between; padding: 3px 0; font-size: 10px; }
          .history-row strong { color: #1e293b; }

          .actions { display: flex; gap: 10px; margin-top: 14px; }
          .actions .btn { flex: 1; text-align: center; padding: 10px; border-radius: 6px; font-weight: 700; font-size: 11px; border: 2px solid; }
          .btn-op { background: #dcfce7; border-color: #10b981; color: #047857; }
          .btn-pause { background: #fef3c7; border-color: #f59e0b; color: #92400e; }
          .btn-qc { background: #dbeafe; border-color: #3b82f6; color: #1e40af; }

          .footer { margin-top: 12px; padding-top: 8px; border-top: 1px solid #e2e8f0; font-size: 8px; color: #94a3b8; text-align: center; }
          .signature { margin-top: 20px; display: flex; justify-content: space-between; gap: 20px; }
          .signature div { flex: 1; text-align: center; font-size: 9px; color: #64748b; }
          .signature .line { border-top: 1px solid #94a3b8; margin: 20px 15px 4px; }

          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .no-print { display: none; } }
        </style>
      </head>
      <body>
        <!-- Header -->
        <div class="header">
      <WeightHistoryDialog open={!!weightHistoryOrder} onOpenChange={(o) => !o && setWeightHistoryOrder(null)} orderId={weightHistoryOrder?.id} orderCode={weightHistoryOrder?.code} />
          <div class="brand">
            <img src="${LOGO_URL}" alt="EP3D" class="logo" />
            <div>
              <div class="brand-title">EP3D ENGINEERING</div>
              <div class="brand-sub">Ordem de Produção · NIF 517 316 188</div>
              <div class="brand-sub">Gerada em ${new Date().toLocaleString("pt-PT")}</div>
            </div>
          </div>
          <div class="qr">
            <img src="${qrUrl}" alt="QR" />
            <div class="qr-lbl">Scan para abrir</div>
          </div>
        </div>

        <!-- Title + Status -->
        <div class="title-row">
          <div>
            <div class="code">${order.code}</div>
            <div class="part-desc">${part?.description || ''}</div>
          </div>
          <div class="status-badge">${statusStyle.dot} ${statusStyle.label}</div>
        </div>

        <!-- Part Info Block -->
        <div class="part-block">
          <div class="photo">
            ${part?.image_url ? `<img src="${part.image_url}" alt="peça" />` : `<div class="placeholder">${part?.code?.slice(0, 3) || '?'}</div>`}
          </div>
          <div class="info">
            <div class="code">${part?.code || '?'}</div>
            <div class="desc">${part?.description || '-'}</div>
            <div class="meta">
              ${part?.plastic_type ? `<span>Plástico: <strong>${part.plastic_type}</strong></span>` : ''}
              ${part?.unit_weight ? `<span>Peso: <strong>${part.unit_weight}g</strong></span>` : ''}
              ${part?.product_type ? `<span>Tipo: <strong>${part.product_type}</strong></span>` : ''}
              ${part?.client ? `<span>Cliente: <strong>${part.client}</strong></span>` : ''}
            </div>
          </div>
        </div>

        <!-- Technical Info Cards -->
        <div class="grid-3">
          <div class="card">
            <div class="card-title">Máquina</div>
            <div class="card-value">${machine?.code || '-'}</div>
            <div class="card-hint">${machine?.description || machine?.model || ''}</div>
          </div>
          <div class="card">
            <div class="card-title">Molde</div>
            <div class="card-value">${mold?.code || '-'}</div>
            <div class="card-hint">${mold?.cavities || 1} cavidade(s) · ${mold?.cycle_time || '-'}s</div>
          </div>
          <div class="card">
            <div class="card-title">Lote MP</div>
            <div class="card-value">${order.raw_material_batch || '-'}</div>
            <div class="card-hint">${order.raw_material_kg ? order.raw_material_kg + ' kg' : ''}</div>
          </div>
        </div>

        <!-- Progress Bar -->
        <div class="progress-block">
          <div class="progress-head">
            <div>
              <div class="lbl">Produção</div>
              <div class="val">${good.toLocaleString()} / ${target.toLocaleString()} peças</div>
            </div>
            <div style="font-size: 32px; font-weight: 900; color: ${progress >= 90 ? '#059669' : progress >= 50 ? '#3730a3' : '#f59e0b'};">${progress.toFixed(1)}%</div>
          </div>
          <div class="progress-bar-outer">
            <div class="progress-bar-inner" style="width: ${progressWidth}%;">
              ${progress > 15 ? Math.round(progress) + '%' : ''}
            </div>
          </div>
        </div>

        <!-- KPIs -->
        <div class="grid-4">
          <div class="kpi">
            <div class="num" style="color:#10b981;">${good.toLocaleString()}</div>
            <div class="lbl">Boas</div>
          </div>
          <div class="kpi">
            <div class="num" style="color:#dc2626;">${rejected.toLocaleString()}</div>
            <div class="lbl">Rejeitadas</div>
          </div>
          <div class="kpi">
            <div class="num" style="color:#1e3a8a;">${timeRemaining}</div>
            <div class="lbl">Tempo restante</div>
          </div>
          <div class="kpi">
            <div class="num" style="color:#7c3aed; font-size: 14px; padding-top: 4px;">${estimatedEnd}</div>
            <div class="lbl">Fim previsto</div>
          </div>
        </div>

        <!-- Checklist -->
        <div class="section">
          <div class="section-title">Checklist de Início de Ordem</div>
          <div class="checklist">
            <ul>
              <li><span class="box"></span> Molde <strong>${mold?.code || '-'}</strong> montado e verificado</li>
              <li><span class="box"></span> Matéria-prima disponível (${order.raw_material_kg || '-'} kg de ${order.raw_material_batch || '-'})</li>
              <li><span class="box"></span> Embalagem disponível (${part?.units_per_bag || 250} peças / saco, ${part?.bags_per_box || 12} sacos / caixa)</li>
              <li><span class="box"></span> Etiquetas impressas e disponíveis</li>
              <li><span class="box"></span> Primeira peça aprovada pela Qualidade</li>
              <li><span class="box"></span> Parâmetros da máquina configurados</li>
              <li><span class="box"></span> Área de trabalho limpa e sinalizada</li>
            </ul>
          </div>
        </div>

        <!-- History -->
        <div class="section">
          <div class="section-title">Histórico</div>
          <div class="history">
            <div class="history-row"><span>Operador atual:</span> <strong>${operator?.name || '—'} ${operator?.user_code ? '(' + operator.user_code + ')' : ''}</strong></div>
            <div class="history-row"><span>Início:</span> <strong>${fmt(order.started_at)}</strong></div>
            <div class="history-row"><span>Fim planeado:</span> <strong>${fmt(order.planned_end)}</strong></div>
            <div class="history-row"><span>Fim previsto:</span> <strong>${estimatedEnd}</strong></div>
            <div class="history-row"><span>Fim real:</span> <strong>${fmt(order.completed_at)}</strong></div>
            <div class="history-row"><span>Paragens registadas:</span> <strong>${stopsCount}</strong></div>
            <div class="history-row"><span>Trocas de operador:</span> <strong>${operatorSwitches}</strong></div>
            ${order.is_delayed_live ? `<div class="history-row" style="color:#dc2626;"><span>⚠ Atraso actual:</span> <strong>${order.delay_minutes_live || 0} min</strong></div>` : ''}
          </div>
        </div>

        <!-- Signature -->
        <div class="signature">
          <div>
            <div class="line"></div>
            Operador
          </div>
          <div>
            <div class="line"></div>
            Chefe de Turno
          </div>
          <div>
            <div class="line"></div>
            Qualidade
          </div>
        </div>

        <div class="footer">
          EP3D ENGINEERING · Ordem ${order.code} · Documento gerado automaticamente pelo Sistema MES · Reservado para uso interno
        </div>
      </body>
      </html>
    `;

    const printWindow2 = printWindow || window.open("", "_blank");
    if (!printWindow2) {
      toast.error("Popup bloqueado pelo browser. Permita popups para gerar PDF.");
      return;
    }
    printWindow2.document.open();
    printWindow2.document.write(printContent);
    printWindow2.document.close();
    setTimeout(() => printWindow2.print(), 800);
  };

  const generateAllOrdersPDF = () => {
    const orderRows = orders.map(order => {
      const part = parts.find(p => p.id === order.part_id);
      const machine = machines.find(m => m.id === order.machine_id);
      const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
      const progress = order.good_quantity_target ? ((order.current_good_quantity || 0) / order.good_quantity_target * 100).toFixed(1) : 0;
      
      return `
        <tr>
          <td style="font-weight: bold;">${order.code}</td>
          <td>${part?.description || '-'}</td>
          <td>${machine?.code || '-'}</td>
          <td><span class="status-badge status-${order.status}">${statusConfig.label}</span></td>
          <td style="text-align: right;">${order.good_quantity_target?.toLocaleString() || 0}</td>
          <td style="text-align: right; color: #16a34a;">${order.current_good_quantity?.toLocaleString() || 0}</td>
          <td style="text-align: right; color: #dc2626;">${order.current_rejected_quantity?.toLocaleString() || 0}</td>
          <td style="text-align: right;">${progress}%</td>
        </tr>
      `;
    }).join('');

    const totals = {
      target: orders.reduce((sum, o) => sum + (o.good_quantity_target || 0), 0),
      good: orders.reduce((sum, o) => sum + (o.current_good_quantity || 0), 0),
      rejected: orders.reduce((sum, o) => sum + (o.current_rejected_quantity || 0), 0),
    };

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Lista de Ordens de Produção</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #1a1a1a; font-size: 12px; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #2563eb; padding-bottom: 20px; margin-bottom: 30px; }
          .logo { height: 50px; background: white; padding: 5px; border-radius: 8px; }
          .header-info { text-align: right; }
          .header-info h1 { font-size: 20px; color: #2563eb; }
          .header-info p { color: #64748b; font-size: 11px; margin-top: 5px; }
          .stats-bar { display: flex; gap: 20px; margin-bottom: 25px; padding: 15px; background: #f8fafc; border-radius: 8px; }
          .stat-item { text-align: center; flex: 1; }
          .stat-value { font-size: 24px; font-weight: 700; }
          .stat-label { font-size: 10px; color: #64748b; text-transform: uppercase; }
          table { width: 100%; border-collapse: collapse; }
          th, td { padding: 10px; text-align: left; border-bottom: 1px solid #e2e8f0; }
          th { background: #f1f5f9; font-weight: 600; text-transform: uppercase; font-size: 10px; color: #64748b; }
          .status-badge { display: inline-block; padding: 3px 10px; border-radius: 12px; color: white; font-weight: 500; font-size: 10px; }
          .status-pending { background: #64748b; }
          .status-running { background: #10b981; }
          .status-paused { background: #f59e0b; }
          .status-completed { background: #3b82f6; }
          .status-archived { background: #9ca3af; }
          .totals-row { font-weight: bold; background: #f8fafc; }
          .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #e2e8f0; font-size: 10px; color: #94a3b8; text-align: center; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="${LOGO_URL}" alt="Logo" class="logo" />
          <div class="header-info">
            <h1>Lista de Ordens de Produção</h1>
            <p>Gerado: ${new Date().toLocaleString("pt-PT")}</p>
            <p>Total: ${orders.length} ordens</p>
          </div>
        </div>

        <div class="stats-bar">
          <div class="stat-item">
            <div class="stat-value">${orders.length}</div>
            <div class="stat-label">Total Ordens</div>
          </div>
          <div class="stat-item">
            <div class="stat-value" style="color: #10b981;">${orders.filter(o => o.status === 'running').length}</div>
            <div class="stat-label">Em Execução</div>
          </div>
          <div class="stat-item">
            <div class="stat-value" style="color: #3b82f6;">${orders.filter(o => o.status === 'completed').length}</div>
            <div class="stat-label">Concluídas</div>
          </div>
          <div class="stat-item">
            <div class="stat-value" style="color: #16a34a;">${totals.good.toLocaleString()}</div>
            <div class="stat-label">Peças Boas</div>
          </div>
          <div class="stat-item">
            <div class="stat-value" style="color: #dc2626;">${totals.rejected.toLocaleString()}</div>
            <div class="stat-label">Rejeitadas</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Código</th>
              <th>Peça</th>
              <th>Máquina</th>
              <th>Estado</th>
              <th style="text-align: right;">Objetivo</th>
              <th style="text-align: right;">Boas</th>
              <th style="text-align: right;">Rej.</th>
              <th style="text-align: right;">Progresso</th>
            </tr>
          </thead>
          <tbody>
            ${orderRows}
            <tr class="totals-row">
              <td colspan="4">TOTAIS</td>
              <td style="text-align: right;">${totals.target.toLocaleString()}</td>
              <td style="text-align: right; color: #16a34a;">${totals.good.toLocaleString()}</td>
              <td style="text-align: right; color: #dc2626;">${totals.rejected.toLocaleString()}</td>
              <td style="text-align: right;">${totals.target > 0 ? (totals.good / totals.target * 100).toFixed(1) : 0}%</td>
            </tr>
          </tbody>
        </table>

        <div class="footer">
          EP3D ENGINEERING PROJECT - Lista de Ordens de Produção
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  const handlePauseOrder = async (orderId) => {
    try {
      await axios.post(`${API}/orders/${orderId}/pause`);
      toast.success("Ordem pausada");
      fetchData();
    } catch (error) {
      showError(error, "Erro ao pausar ordem");
    }
  };

  const handleCompleteOrder = async (orderId) => {
    if (!window.confirm("Tem a certeza que deseja concluir esta ordem?")) return;
    
    try {
      await axios.post(`${API}/orders/${orderId}/complete`);
      toast.success("Ordem concluída");
      fetchData();
    } catch (error) {
      showError(error, "Erro ao concluir ordem");
    }
  };

  const getPartName = (partId) => {
    const part = parts.find((p) => p.id === partId);
    return part ? part.code : "N/A";
  };

  const getMoldName = (moldId) => {
    const mold = molds.find((m) => m.id === moldId);
    return mold ? mold.code : "N/A";
  };

  const getMachineName = (machineId) => {
    const machine = machines.find((m) => m.id === machineId);
    return machine ? machine.code : "N/A";
  };

  // Filter molds based on selected part
  const filteredMolds = formData.part_id
    ? molds.filter((m) => m.part_id === formData.part_id)
    : [];

  const handleDeleteOrder = async (orderId, orderCode) => {
    if (!window.confirm(`Tem a certeza que deseja APAGAR a ordem ${orderCode}?\n\nEsta ação não pode ser desfeita!`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      toast.success(`Ordem ${orderCode} apagada com sucesso`);
      fetchData();
    } catch (error) {
      showError(error, "Erro ao apagar ordem");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl md:text-4xl font-bold tracking-tight">
            Ordens de Produção
          </h1>
          <p className="text-muted-foreground mt-1">
            Gerir ordens de fabrico
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={generateAllOrdersPDF} disabled={orders.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            Exportar Todas (PDF)
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => resetForm()} data-testid="add-order-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nova Ordem
              </Button>
            </DialogTrigger>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border pb-2 mb-4">
        <Button
          variant={activeTab === "active" ? "default" : "ghost"}
          onClick={() => setActiveTab("active")}
          className="gap-2"
        >
          <ClipboardList className="h-4 w-4" />
          Ordens Ativas ({activeOrders.length})
        </Button>
        <Button
          variant={activeTab === "history" ? "default" : "ghost"}
          onClick={() => setActiveTab("history")}
          className="gap-2"
        >
          <Archive className="h-4 w-4" />
          Histórico ({historyOrders.length})
        </Button>
      </div>

          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-chivo">
                {editMode ? "Editar Ordem de Produção" : "Nova Ordem de Produção"}
              </DialogTitle>
              <DialogDescription>
                {editMode 
                  ? "Altere os dados da ordem. Os cálculos são atualizados automaticamente."
                  : "Defina a peça, molde, máquina e quantidade. Os cálculos são automáticos."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="part_id">Peça *</Label>
                  <select
                    id="part_id"
                    value={formData.part_id}
                    onChange={(e) => {
                      const partId = e.target.value;
                      const part = parts.find(p => p.id === partId);
                      const upb = part?.units_per_bag || 0;
                      const bpb = part?.bags_per_box || 0;
                      const piecesPerBox = upb * bpb;
                      setFormData(prev => ({
                        ...prev,
                        part_id: partId,
                        mold_id: "",
                        // Auto-fill packaging from part (apenas se utilizador nao tiver mudado manualmente)
                        pieces_per_package: piecesPerBox ? String(piecesPerBox) : prev.pieces_per_package,
                        packages_per_pallet: part?.boxes_per_pallet ? String(part.boxes_per_pallet) : prev.packages_per_pallet,
                        packaging_notes: part && upb && bpb
                          ? `${upb} un/saco × ${bpb} sacos/caixa = ${piecesPerBox} un/caixa${part.boxes_per_pallet ? ` × ${part.boxes_per_pallet} caixas/palete = ${piecesPerBox * part.boxes_per_pallet} un/palete` : ''}`
                          : prev.packaging_notes,
                      }));
                      if (part && (upb || bpb || part.boxes_per_pallet)) {
                        toast.success(`📦 Embalagem da peça ${part.code} carregada automaticamente`);
                      }
                    }}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    required
                    data-testid="order-part"
                  >
                    <option value="">Selecione uma peça</option>
                    {parts.map((part) => (
                      <option key={part.id} value={part.id}>
                        {part.code} - {part.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mold_id">Molde *</Label>
                  <select
                    id="mold_id"
                    value={formData.mold_id}
                    onChange={(e) => setFormData({ ...formData, mold_id: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    required
                    disabled={!formData.part_id}
                    data-testid="order-mold"
                  >
                    <option value="">
                      {formData.part_id ? "Selecione um molde" : "Selecione primeiro a peça"}
                    </option>
                    {filteredMolds.map((mold) => (
                      <option key={mold.id} value={mold.id}>
                        {mold.code} ({mold.cavities} cav.)
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="client_id">Cliente</Label>
                  <div className="flex gap-1">
                    <select
                      id="client_id"
                      value={formData.client_id}
                      onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                      className="flex-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="order-client"
                    >
                      <option value="">Sem cliente (opcional)</option>
                      {clients.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}{c.nif ? ` (${c.nif})` : ""}</option>
                      ))}
                    </select>
                    <Button type="button" variant="outline" size="icon" onClick={() => setShowNewClientDialog(true)} data-testid="new-client-btn" title="Criar novo cliente">
                      +
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="machine_id">Máquina *</Label>
                  <select
                    id="machine_id"
                    value={formData.machine_id}
                    onChange={(e) => setFormData({ ...formData, machine_id: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    required
                    data-testid="order-machine"
                  >
                    <option value="">Selecione uma máquina</option>
                    {machines.filter((m) => m.status !== "maintenance").map((machine) => (
                      <option key={machine.id} value={machine.id}>
                        {machine.code} - {machine.description} ({machine.tonnage}T)
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="good_quantity_target">Quantidade Boa a Produzir *</Label>
                  <Input
                    id="good_quantity_target"
                    type="number"
                    min="1"
                    value={formData.good_quantity_target}
                    onChange={(e) => setFormData({ ...formData, good_quantity_target: e.target.value })}
                    placeholder="10000"
                    required
                    data-testid="order-quantity"
                  />
                </div>
              </div>

              {/* BOM Component Availability */}
              {(checkingBom || bomAvailability) && (
                <div className={`border-2 rounded-lg p-3 ${bomAvailability?.can_produce ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"}`} data-testid="bom-availability">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-sm flex items-center gap-2">
                      📦 Estrutura do Produto (BOM)
                      {checkingBom && <span className="text-xs text-muted-foreground">verificando...</span>}
                    </h4>
                    {bomAvailability && (
                      bomAvailability.can_produce ? (
                        <span className="text-xs bg-emerald-500 text-white px-2 py-1 rounded font-bold">✓ Stock suficiente para todos os componentes</span>
                      ) : (
                        <span className="text-xs bg-amber-500 text-white px-2 py-1 rounded font-bold">⚠ Faltam {bomAvailability.components_short_count} componentes</span>
                      )
                    )}
                  </div>
                  {bomAvailability && (
                    <div className="text-xs">
                      <div className="grid grid-cols-6 gap-1 font-bold uppercase text-slate-500 border-b pb-1 mb-1">
                        <div>Código</div><div className="col-span-2">Descrição</div><div>Tipo</div><div className="text-right">Nec.</div><div className="text-right">Stock/Falta</div>
                      </div>
                      {(bomAvailability.flat_components || []).map(c => (
                        <div key={c.part_id} className={`grid grid-cols-6 gap-1 py-1 border-b border-slate-100 ${c.shortage_qty > 0 ? "text-rose-700" : ""}`}>
                          <div className="font-mono font-bold">{c.code}</div>
                          <div className="col-span-2 truncate">{c.description}</div>
                          <div className="text-[10px]">{c.product_type}</div>
                          <div className="text-right font-bold">{c.required_qty}</div>
                          <div className="text-right font-semibold">
                            {c.stock_qty}
                            {c.shortage_qty > 0 && <span className="text-rose-600"> / faltam {c.shortage_qty}</span>}
                          </div>
                        </div>
                      ))}
                      {(bomAvailability.flat_components || []).length === 0 && (
                        <div className="text-center text-muted-foreground py-2">Este produto não tem BOM definido. Configure em <strong>BOM / Estrutura</strong>.</div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="raw_material_batch">Lote de Matéria-Prima</Label>
                <Input
                  id="raw_material_batch"
                  value={formData.raw_material_batch}
                  onChange={(e) => setFormData({ ...formData, raw_material_batch: e.target.value })}
                  placeholder="Ex: LOT-2024-001"
                  data-testid="order-batch"
                />
              </div>

              {/* Packaging Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  📦 Informação de Embalagem
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de Embalagem</Label>
                    <select
                      className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                      value={formData.packaging_type_id}
                      onChange={(e) => setFormData({ ...formData, packaging_type_id: e.target.value })}
                      data-testid="order-packaging-type"
                    >
                      <option value="">Selecione o tipo de embalagem</option>
                      {packagingTypes.map((pt) => (
                        <option key={pt.id} value={pt.id}>
                          {pt.code} - {pt.description} (cap. {pt.capacity})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Peças por Embalagem</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.pieces_per_package}
                      onChange={(e) => setFormData({ ...formData, pieces_per_package: e.target.value })}
                      placeholder="Ex: 100"
                      data-testid="order-pieces-package"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Embalagens por Palete</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.packages_per_pallet}
                      onChange={(e) => setFormData({ ...formData, packages_per_pallet: e.target.value })}
                      placeholder="Ex: 48"
                      data-testid="order-packages-pallet"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Notas de Embalagem</Label>
                    <Input
                      value={formData.packaging_notes}
                      onChange={(e) => setFormData({ ...formData, packaging_notes: e.target.value })}
                      placeholder="Instruções especiais..."
                      data-testid="order-packaging-notes"
                    />
                  </div>
                </div>
              </div>

              {/* Planning Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  📅 Planeamento de Produção
                </h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Se definir uma data de início, a ordem será automaticamente adicionada ao planeamento.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data de Início Planeado</Label>
                    <Input
                      type="date"
                      value={formData.planned_start}
                      onChange={(e) => setFormData({ ...formData, planned_start: e.target.value })}
                      data-testid="order-planned-start"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Horas de Trabalho por Dia</Label>
                    <select
                      className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                      value={formData.hours_per_day}
                      onChange={(e) => setFormData({ ...formData, hours_per_day: e.target.value })}
                      data-testid="order-hours-per-day"
                    >
                      <option value="8">8 horas (1 turno)</option>
                      <option value="16">16 horas (2 turnos)</option>
                      <option value="24">24 horas (3 turnos)</option>
                    </select>
                  </div>
                </div>
                {calculations && formData.planned_start && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>📊 Previsão:</strong> Esta ordem vai ocupar a máquina por <strong>{calculations.estimated_days} dia(s)</strong> a partir de {new Date(formData.planned_start).toLocaleDateString('pt-PT')}.
                    </p>
                  </div>
                )}
              </div>
              
              {/* Automatic Calculations */}
              {calculations && (
                <Card className="bg-secondary/30 border-border/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Calculator className="h-4 w-4" />
                      Cálculos Automáticos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Qtd. Total</p>
                        <p className="font-mono font-bold">{calculations.total_quantity.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Nº Ciclos</p>
                        <p className="font-mono font-bold">{calculations.total_cycles.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Tempo Total</p>
                        <p className="font-mono font-bold">{calculations.total_time_hours}h</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Dias Estimados</p>
                        <p className="font-mono font-bold text-blue-600">{calculations.estimated_days} dia(s)</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Peso/Ciclo</p>
                        <p className="font-mono font-bold">{calculations.weight_per_cycle}g</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">MP Necessária</p>
                        <p className="font-mono font-bold">{calculations.raw_material_kg} kg</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-order-btn">
                  Criar Ordem
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Orders Table */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="font-chivo flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Lista de Ordens ({orders.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {orders.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Peça</TableHead>
                    <TableHead>Molde</TableHead>
                    <TableHead>Máquina</TableHead>
                    <TableHead>Objetivo</TableHead>
                    <TableHead>Produzido</TableHead>
                    <TableHead>Matéria-Prima</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(activeTab === "active" ? activeOrders : historyOrders).map((order) => {
                    const statusConfig = STATUS_CONFIG[order.status];
                    const progress = order.good_quantity_target > 0
                      ? ((order.current_good_quantity / order.good_quantity_target) * 100).toFixed(1)
                      : 0;
                    return (
                      <TableRow key={order.id} data-testid={`order-row-${order.code}`} className={`${order.priority === "urgent" ? "bg-red-50/60" : ""} ${highlightCode === order.code || highlightCode === order.id ? "ring-2 ring-blue-500 bg-blue-50/70" : ""}`}>
                        <TableCell className="font-mono font-bold">
                          <div className="flex items-center gap-2">
                            {order.code}
                            {order.priority && order.priority !== "normal" && PRIORITY_CONFIG[order.priority] && (
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${PRIORITY_CONFIG[order.priority].className}`} data-testid={`priority-badge-${order.code}`}>
                                {PRIORITY_CONFIG[order.priority].icon} {PRIORITY_CONFIG[order.priority].label}
                              </span>
                            )}
                          </div>
                          {order.suspend_reason && <p className="text-[10px] text-orange-600 mt-0.5 italic">Suspensa: {order.suspend_reason}</p>}
                          {order.cancel_reason && <p className="text-[10px] text-red-600 mt-0.5 italic">Cancelada: {order.cancel_reason}</p>}
                        </TableCell>
                        <TableCell>{getPartName(order.part_id)}</TableCell>
                        <TableCell>{getMoldName(order.mold_id)}</TableCell>
                        <TableCell>{getMachineName(order.machine_id)}</TableCell>
                        <TableCell className="font-mono">
                          {order.good_quantity_target.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-emerald-500">
                              {order.current_good_quantity.toLocaleString()}
                            </span>
                            <span className="text-muted-foreground">/</span>
                            <span className="font-mono text-red-500">
                              {order.current_rejected_quantity.toLocaleString()}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              ({progress}%)
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <MaterialBalanceCell balance={materialBalance[order.id]} code={order.code} />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 flex-wrap">
                            <Badge className={`${statusConfig.color} text-white`}>
                              {statusConfig.label}
                            </Badge>
                            {order.is_delayed_live && order.delay_minutes_live > 0 && (
                              <Badge className="bg-red-500 text-white text-xs animate-pulse" title={`Atraso previsto: ${order.delay_minutes_live} min. Fim estimado: ${order.estimated_end_time ? new Date(order.estimated_end_time).toLocaleString("pt-PT") : "-"}`}>
                                ⚠️ {order.delay_minutes_live < 60 ? `${order.delay_minutes_live}m` : `${(order.delay_minutes_live/60).toFixed(1)}h`}
                              </Badge>
                            )}
                            {order.is_delayed && order.delay_days > 0 && !order.is_delayed_live && (
                              <Badge className="bg-amber-500 text-white text-xs" title={`Atrasada ${order.delay_days} dia(s)`}>
                                🕓 {order.delay_days}d
                              </Badge>
                            )}
                            {order.propagated_delay_minutes > 0 && order.status !== "running" && (
                              <Badge className="bg-orange-500 text-white text-xs" title={`Inicio empurrado +${order.propagated_delay_minutes} min por ordens anteriores`}>
                                ↪ +{order.propagated_delay_minutes}m
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {/* Edit button - available for active orders only */}
                            {activeTab === "active" && order.status !== "completed" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditDialog(order)}
                                className="text-blue-500 hover:text-blue-400"
                                title="Editar Ordem"
                                data-testid={`edit-order-${order.code}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setWeightHistoryOrder(order)}
                              className="text-teal-600 hover:text-teal-500"
                              title="Histórico de Pesagens"
                              data-testid={`weight-history-${order.code}`}
                            >
                              <Scale className="h-4 w-4" />
                            </Button>
                            {order.status === "pending" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleStartOrder(order.id)}
                                className="text-emerald-500 hover:text-emerald-400"
                                title="Iniciar"
                                data-testid={`start-order-${order.code}`}
                              >
                                <Play className="h-4 w-4" />
                              </Button>
                            )}
                            {order.status === "running" && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handlePauseOrder(order.id)}
                                  className="text-amber-500 hover:text-amber-400"
                                  title="Pausar"
                                  data-testid={`pause-order-${order.code}`}
                                >
                                  <Pause className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleCompleteOrder(order.id)}
                                  className="text-blue-500 hover:text-blue-400"
                                  title="Concluir"
                                  data-testid={`complete-order-${order.code}`}
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            {order.status === "paused" && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleStartOrder(order.id)}
                                  className="text-emerald-500 hover:text-emerald-400"
                                  title="Retomar"
                                  data-testid={`resume-order-${order.code}`}
                                >
                                  <Play className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleCompleteOrder(order.id)}
                                  className="text-blue-500 hover:text-blue-400"
                                  title="Concluir"
                                  data-testid={`complete-paused-order-${order.code}`}
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => generateOrderPDF(order)}
                              className="text-gray-500 hover:text-gray-700"
                              title="Exportar PDF"
                              data-testid={`print-order-${order.code}`}
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={async () => {
                                try {
                                  const res = await axios.get(`${API}/orders/${order.id}/packing-criteria`);
                                  openPackingSlip(res.data);
                                } catch (e) { showError(e, "Erro ao abrir folha de embalagem"); }
                              }}
                              className="text-orange-500 hover:text-orange-700"
                              title="Folha de Embalagem"
                              data-testid={`packing-slip-${order.code}`}
                            >
                              <Package className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/orders/${order.id}/report`)}
                              className="text-blue-500 hover:text-blue-700"
                              title="Relatório da Ordem"
                              data-testid={`report-order-${order.code}`}
                            >
                              <BarChart3 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setAuditOrder(order); setShowAudit(true); fetchAuditLog(order.id); }}
                              className="text-indigo-500 hover:text-indigo-700"
                              title="Historico + Timeline"
                              data-testid={`audit-order-${order.code}`}
                            >
                              <History className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setTimelineOrderId(order.id); setShowTimeline(true); }}
                              className="text-blue-500 hover:text-blue-700"
                              title="Timeline"
                              data-testid={`timeline-order-${order.code}`}
                            >
                              <Activity className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setPhotosOrder(order)}
                              className="text-rose-500 hover:text-rose-700"
                              title="Fotos da Ordem"
                              data-testid={`photos-order-${order.code}`}
                            >
                              <Camera className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setPackagingOrder(order)}
                              className="text-emerald-500 hover:text-emerald-700"
                              title="Progresso de Embalagem"
                              data-testid={`packaging-order-${order.code}`}
                            >
                              <Package className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setNotesOrder(order)}
                              className="text-amber-500 hover:text-amber-700"
                              title="Notas de Turno"
                              data-testid={`notes-order-${order.code}`}
                            >
                              <StickyNote className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteOrder(order.id, order.code)}
                              className="text-red-500 hover:text-red-700"
                              title="Apagar Ordem"
                              data-testid={`delete-order-${order.code}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          {/* P0/P1 Actions: suspend/cancel/resume/split/priority */}
                          {!["completed", "cancelled", "archived"].includes(order.status) && (
                            <div className="mt-2 flex justify-end">
                              <OrderActions order={order} onDone={fetchData} />
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-12 text-center text-muted-foreground">
              <ClipboardList className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">Nenhuma ordem registada</p>
              <p className="text-sm">Clique em "Nova Ordem" para começar</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Timeline Dialog */}
      <Dialog open={showTimeline} onOpenChange={setShowTimeline}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="timeline-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-blue-600" />
              Timeline da Ordem
            </DialogTitle>
          </DialogHeader>
          {timelineOrderId && <OrderTimeline orderId={timelineOrderId} />}
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={showAudit} onOpenChange={setShowAudit}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-indigo-600" />
              Historico de Alteracoes
            </DialogTitle>
            {auditOrder && <p className="text-xs text-gray-500 mt-1">Ordem {auditOrder.code}</p>}
          </DialogHeader>
          <div className="mt-3">
            {auditLoading ? (
              <div className="text-center py-8 text-gray-400 text-sm">A carregar...</div>
            ) : auditLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">Sem registos historicos</div>
            ) : (
              <div className="relative pl-6 border-l-2 border-indigo-200 space-y-3">
                {auditLogs.map((log, i) => {
                  const actionLabels = {
                    create: { label: "Criada", color: "bg-green-100 text-green-800 border-green-300" },
                    update: { label: "Editada", color: "bg-blue-100 text-blue-800 border-blue-300" },
                    delete: { label: "Eliminada", color: "bg-red-100 text-red-800 border-red-300" },
                    start: { label: "Iniciada", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
                    pause: { label: "Pausada", color: "bg-amber-100 text-amber-800 border-amber-300" },
                    complete: { label: "Concluida", color: "bg-purple-100 text-purple-800 border-purple-300" },
                    cancel: { label: "Cancelada", color: "bg-red-100 text-red-800 border-red-300" },
                    quality_block: { label: "Bloqueada Qual.", color: "bg-rose-100 text-rose-800 border-rose-300" },
                    quality_unblock: { label: "Desbloqueada", color: "bg-teal-100 text-teal-800 border-teal-300" },
                    production_record: { label: "Producao Reg.", color: "bg-sky-100 text-sky-800 border-sky-300" },
                    stop_start: { label: "Paragem Inicio", color: "bg-orange-100 text-orange-800 border-orange-300" },
                    stop_end: { label: "Paragem Fim", color: "bg-lime-100 text-lime-800 border-lime-300" },
                    manual_stop: { label: "Paragem Manual", color: "bg-amber-100 text-amber-900 border-amber-400" },
                  };
                  const meta = actionLabels[log.action] || { label: log.action, color: "bg-gray-100 text-gray-800 border-gray-300" };
                  return (
                    <div key={i} className="relative" data-testid={`audit-log-${i}`}>
                      <div className="absolute -left-[31px] top-1 w-4 h-4 rounded-full bg-indigo-500 border-4 border-white"></div>
                      <div className="bg-white border rounded-lg p-3">
                        <div className="flex items-start justify-between flex-wrap gap-2 mb-1">
                          <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded border ${meta.color}`}>{meta.label}</span>
                          <span className="text-xs text-gray-500 font-mono">{new Date(log.timestamp).toLocaleString("pt-PT")}</span>
                        </div>
                        <div className="text-sm text-gray-700">
                          Por <strong>{log.user_name}</strong>
                          {log.entity_type !== "order" && <span className="text-xs text-gray-500 ml-1">({log.entity_type})</span>}
                        </div>
                        {log.new_value && Object.keys(log.new_value).length > 0 && (
                          <details className="mt-1.5 text-xs text-gray-600">
                            <summary className="cursor-pointer hover:text-indigo-700">Detalhes</summary>
                            <pre className="bg-slate-50 rounded p-2 mt-1 overflow-x-auto text-[10px]">{JSON.stringify(log.new_value, null, 2)}</pre>
                          </details>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Photos Dialog */}
      <Dialog open={!!photosOrder} onOpenChange={(o) => { if (!o) setPhotosOrder(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="photos-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-rose-600" />
              Fotos da Ordem
            </DialogTitle>
            {photosOrder && <p className="text-xs text-gray-500">Ordem {photosOrder.code} · {photosOrder.part_code}</p>}
          </DialogHeader>
          {photosOrder && <OrderPhotos orderId={photosOrder.id} />}
        </DialogContent>
      </Dialog>

      {/* Packaging Progress Dialog */}
      <Dialog open={!!packagingOrder} onOpenChange={(o) => { if (!o) setPackagingOrder(null); }}>
        <DialogContent className="max-w-2xl" data-testid="packaging-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-emerald-600" />
              Progresso de Embalagem
            </DialogTitle>
            {packagingOrder && <p className="text-xs text-gray-500">Ordem {packagingOrder.code} · {packagingOrder.part_code}</p>}
          </DialogHeader>
          {packagingOrder && <div className="mt-3"><PackagingProgress orderId={packagingOrder.id} /></div>}
        </DialogContent>
      </Dialog>

      {/* Shift Notes Dialog */}
      <Dialog open={!!notesOrder} onOpenChange={(o) => { if (!o) setNotesOrder(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="notes-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="h-5 w-5 text-amber-600" />
              Notas entre Turnos
            </DialogTitle>
            {notesOrder && <p className="text-xs text-gray-500">Ordem {notesOrder.code} · {notesOrder.part_code}</p>}
          </DialogHeader>
          {notesOrder && <ShiftNotes orderId={notesOrder.id} machineId={notesOrder.machine_id} />}
        </DialogContent>
      </Dialog>

      {/* Novo cliente (rápido) */}
      <ClientQuickCreateDialog
        open={showNewClientDialog}
        onClose={() => setShowNewClientDialog(false)}
        onCreated={(c) => {
          if (!c) return;
          // adicionar à lista se ainda não estiver e selecioná-lo
          setClients((prev) => (prev.some(x => x.id === c.id) ? prev : [...prev, c]));
          setFormData((f) => ({ ...f, client_id: c.id }));
        }}
      />
    </div>
  );
}
